import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import orderBy from 'lodash.orderby';
import cx from 'classnames';

import Message, { getMessage } from '../../components/common/message/Message';
import {Select} from '../../components/common/select/Select';
import RedeemError from '../../components/redeem/RedeemError';

import prizeActions from '../../actions/PrizeActions';
import taxProfileActions from '../../actions/TaxProfileActions';

import {SORT_TYPE} from '../../constants/SortTypes';
import {PAYMENT_STATUS, TAX_PROFILE_STATUS} from '../../constants/PaymentStatus';
import {getURLParam, processUserAge} from '../../utils/Utils';
import {PrizeType} from '../../constants/PrizeType';
import {ACCOUNT_STATUS} from '../../constants/PayoutConstants';
import { withContentGated } from '../../components/access/LegalAccess';

import {
    AccountId,
    Heading,
    LoginWrapper,
    PrizeList,
    PrizeSpacer,
    Spacer,
    Tabs,
    TaxProfile,
    Text
} from '../../components';
import { GAME_TITLE } from '../../constants/GameTitle';
import moment from 'moment/moment';

const getDate = i => Date.parse(i.dateOfEvent);

const TAB = {
    COMPETITIVE: 'competitive',
    TAX_PROFILE: 'tax_profile',
    INTERVIEW: 'interview'
};

const getPaidStatus = i => {
    if (i.prizeType === PrizeType.PHYSICAL_GOODS) {
        return i.markedOfflinePay ? 1 : 0;
    }
    return (i.paymentStatus === PAYMENT_STATUS.COMPLETED || i.markedOfflinePay) ? 1 : 0;
};

const byRecent = items => orderBy(items, [getDate], ['desc']);
const byGameTitle = items => orderBy(items, ['gameId', getDate], ['asc', 'desc']);
const byUnpaid = items => orderBy(items, [getPaidStatus, getDate], ['asc', 'desc']);
const byPaid = items => orderBy(items, [getPaidStatus, getDate], ['desc', 'desc']);

const sortPrizes = (prizes, sortType) => {
    if (sortType === SORT_TYPE.MOST_RECENT) {
        return byRecent(prizes);
    } else if (sortType === SORT_TYPE.GAME_TITLE) {
        return byGameTitle(prizes);
    } else if (sortType === SORT_TYPE.UNPAID_TO_PAID) {
        return byUnpaid(prizes);
    } else if (sortType === SORT_TYPE.PAID_TO_UNPAID) {
        return byPaid(prizes);
    }
    return prizes;
};

const handleOnEntrance = (tab, history = null) => {
    let tabIndex;
    switch (tab) {
        case TAB.COMPETITIVE: {
            tabIndex = 0;
            break;
        }
        case TAB.TAX_PROFILE: {
            tabIndex = 1;
            break;
        }
        case TAB.INTERVIEW: {
            if (history) {
                history.push('interview', { isRetake: false });
            }
            break;
        }
        default:
            tabIndex = 0;
    }
    return tabIndex;
};

const mapDispatchToProps = dispatch => {
    return {
        getInterviewUrl: locale => dispatch(taxProfileActions.getInterview(locale)),
        getPrizes: payload => dispatch(prizeActions.getPrizes(payload)),
        getAccountStatus: () => dispatch(taxProfileActions.getAccountStatus()),
        getTaxProfile: payload => dispatch(taxProfileActions.getTaxProfile(payload)),
        getTaxRetakeAvailable: () => dispatch(taxProfileActions.getTaxRetakeAvailable())
    };
};

const mapStateToProps = state => {
    return {
        configState: state.get('config').toJS(),
        locale: state.get('localization').get('locale'),
        messages: state.get('localization').get('messageProperties'),
        prizeState: state.get('prize').toJS(),
        taxProfileState: state.get('taxProfile').toJS(),
        userState: state.get('user').toJS(),
        legalState: state.get('legal').toJS()
    };
};
@connect(
    mapStateToProps,
    mapDispatchToProps
)
@withContentGated(GAME_TITLE.FORTNITE)
export default class CompetitiveView extends Component {
    static displayName = 'CompetitiveView';

    static propTypes = {
        configState: PropTypes.object,
        getPrizes: PropTypes.func,
        getTaxProfile: PropTypes.func,
        locale: PropTypes.string,
        messages: PropTypes.object,
        prizeState: PropTypes.object,
        taxProfileState: PropTypes.object,
        userState: PropTypes.object,
        history: PropTypes.object,
        getAccountStatus: PropTypes.object,
        getTaxRetakeAvailable: PropTypes.func
    };

    constructor(props) {
        super(props);
        const tab = getURLParam('tab');
        this.state = {
            sortType: SORT_TYPE.MOST_RECENT,
            tabIndex: handleOnEntrance(tab, props.history)
        };

        this.getMsg = this.getMsg.bind(this);
        this.handleOnSort = this.handleOnSort.bind(this);
        this.prizeTabRef = React.createRef();
    }

    componentDidMount() {
        const { locale, getPrizes, getTaxProfile, getAccountStatus, getTaxRetakeAvailable } = this.props;
        getPrizes({ lang: locale });
        getTaxProfile();
        getAccountStatus();
        getTaxRetakeAvailable();
    }

    componentDidUpdate(prevProps) {
        const { taxProfileState: prevTaxProfileState } = prevProps;
        const { taxProfileState } = this.props;

        if (!prevTaxProfileState.wasUpdated && taxProfileState.wasUpdated) {
            const { locale, getPrizes } = this.props;
            // tax profile has been updated, re-fetch prizes
            getPrizes({ lang: locale });
        }
    }

    handleOnSort(evt) {
        this.setState({sortType: evt.target.value, tabIndex: handleOnEntrance(TAB.COMPETITIVE)});
    }

    getMsg(code, args) {
        return getMessage(this.props.messages, code, args);
    }

    getExpireWarnMsg = () => {
        const {taxProfileState: {form}} = this.props;
        if (form.taxStatusExpiryDate) {
            const currentDate = moment();
            const expiryDate = moment(form.taxStatusExpiryDate);
            if (expiryDate.isBefore(currentDate.add(3, 'months'))) {
                return ['epic.esport.profile.tax.expire.message', expiryDate.format('MMM D, YYYY')];
            }
        }
        return '';
    }

    getAcctStatus = () => {
        const {taxProfileState: {accountStatus}} = this.props;
        if (!accountStatus) {
            return null;
        }
        let code = '';
        switch (accountStatus) {
            case ACCOUNT_STATUS.PENDING_ACCOUNT_ACTIVATION:
                code = 'epic.esport.profile.account.active';
                break;
            case ACCOUNT_STATUS.ACCOUNT_PENDING_VERIFICATION:
                code = 'epic.esport.profile.account.verification';
                break;
            case ACCOUNT_STATUS.PENDING_TRANSFER_METHOD_ACTION:
                code = 'epic.esport.profile.account.transfer';
                break;
            default:
                break;
        }
        return code;
    }

    getHeaderMsg = () => {
        const { taxProfileState } = this.props;
        const { status } = taxProfileState;
        let [code, tab] = [];
        switch (status) {
            case TAX_PROFILE_STATUS.NULL:
                [code, tab] = [
                    'epic.esport.profile.header.message.invalidated',
                    `${this.getMsg('epic.esport.prizes.tab.prize')}`
                ];
                break;
            case TAX_PROFILE_STATUS.NAME_MISS_MATCH:
            case TAX_PROFILE_STATUS.INVALID_TAX_INFO:
            case TAX_PROFILE_STATUS.MISSING_TAX_INFO:
            case TAX_PROFILE_STATUS.DEACTIVATED:
            case TAX_PROFILE_STATUS.TAX_INFO_REQUIRES_UPDATE:
                if (taxProfileState && taxProfileState.retakeAvailable) {
                    [code, tab] = [
                        'epic.esport.profile.header.message.requiresUpdate',
                        this.getMsg('epic.esport.prizes.tab.tax')
                    ];
                } else {
                    [code, tab] = [
                        'epic.esport.profile.header.message.invalidated',
                        `${this.getMsg('epic.esport.prizes.tab.prize')}`
                    ];
                }
                break;
            default:
                break;
        }
        if (code) {
            return this.getMsg(code, [tab]);
        }
        return null;
    }

    GlobalHeader = () => {
        const __htmlMsg = this.getHeaderMsg();
        const acctStatusCode = this.getAcctStatus();
        const [expireWarnMsg, expireDate] = this.getExpireWarnMsg();
        const isMultiLine = (__htmlMsg && acctStatusCode);
        if (__htmlMsg || acctStatusCode || expireWarnMsg) {
            return (<div key="headers" className="acct————global__header">
                <ul className={cx(
                    'container',
                    isMultiLine && 'is-multi'
                )}>
                    {
                        __htmlMsg && <li dangerouslySetInnerHTML={{ __html: __htmlMsg }} />
                    }
                    {
                        acctStatusCode && (<li>
                            {this.getMsg(acctStatusCode)}
                        </li>)
                    }
                    {
                        expireWarnMsg && (<li>
                            {this.getMsg(expireWarnMsg, [expireDate])}
                        </li>)
                    }
                </ul>
            </div>);
        }
        return null;
    }

    render() {
        const {
            configState,
            messages,
            prizeState,
            userState,
            history,
            taxProfileState
        } = this.props;

        const { isTaxProfileActive } = configState;
        const { accountInfo: { displayName, dateOfBirth } } = userState;
        const { supportUrl } = configState;
        const supportSiteName = this.getMsg('epic.common.support_site');
        const sortedPrizes = sortPrizes(processUserAge(prizeState.winnings, dateOfBirth), this.state.sortType);
        const firstCanRetakePrize = sortedPrizes.find(prize => {
            return !!prize.taxInterviewUrl;
        });
        const firstCanRetakePrizeIndex = sortedPrizes.findIndex(prize => {
            return !!prize.taxInterviewUrl;
        });

        const jumpToTab = () => {
            if (this.prizeTabRef && this.prizeTabRef.current) {
                this.prizeTabRef.current.onTabChange(0);
            }
            setTimeout(() => {
                const prizeEls = document.querySelectorAll('.competitive-prize-list .prize__root');
                if (prizeEls[firstCanRetakePrizeIndex]) {
                    const canRetakePrizeEl = prizeEls[firstCanRetakePrizeIndex].querySelector('button.expandbtn__prize');
                    canRetakePrizeEl.click();
                }
            });
        };

        if (!userState.isLoggedIn) {
            return <LoginWrapper productName="fortnite" />;
        }

        if (userState.hasEsportEntitlement === false) {
            return (
                <RedeemError
                    supportName={supportSiteName}
                    supportUrl={supportUrl}
                />
            );
        }

        return (
            <div className="container acct__root" id="content" key="content">
                <this.GlobalHeader key="header" />
                <Spacer as="div" className="mbl__spacer" padding={40}>
                    <Heading
                        as="h1"
                        bottom={40}
                        className="acct__title"
                        size={24}
                        weight="normal"
                    >
                        {this.getMsg('epic.esport.prizes.header.title')}
                    </Heading>
                    <div className="acct__top">
                        <Message
                            as="div"
                            className="acct__intro"
                            args={[supportUrl, supportSiteName]}
                            code="epic.esport.prizes.header.content"
                        />
                        <AccountId
                            id={userState.accountInfo.id}
                            showLabel={this.getMsg('epic.esport.prizes.header.id.show')}
                            hideLabel={this.getMsg('epic.esport.prizes.header.id.hide')}
                        />
                    </div>
                </Spacer>

                <PrizeSpacer>
                    <Heading>
                        {this.getMsg('epic.esport.prizes.board.title')}
                    </Heading>
                    <Text size={14}>
                        {this.getMsg('epic.esport.prizes.board.content')}
                    </Text>
                </PrizeSpacer>

                <div className="es-options">
                    <Tabs
                        ref={this.prizeTabRef}
                        index={this.state.tabIndex}
                        header={
                            <Select
                                className="prize__sort"
                                desc={this.getMsg('epic.esport.common.sort_by')}
                                name="sortBy"
                                onChange={this.handleOnSort}
                                options={[
                                    {
                                        value: SORT_TYPE.MOST_RECENT,
                                        optionLabel: this.getMsg('epic.esport.common.most_recent')
                                    },
                                    {
                                        value: SORT_TYPE.GAME_TITLE,
                                        optionLabel: this.getMsg('epic.esport.common.game_title')
                                    },
                                    {
                                        value: SORT_TYPE.UNPAID_TO_PAID,
                                        optionLabel: this.getMsg('epic.esport.common.unpaid_to_paid')
                                    },
                                    {
                                        value: SORT_TYPE.PAID_TO_UNPAID,
                                        optionLabel: this.getMsg('epic.esport.common.paid_to_unpaid')
                                    }
                                ]}
                            />
                        }
                        headerAvailable={[0]}
                    >
                        <Tabs.Tab title={this.getMsg('epic.esport.prizes.tab.prize')}>
                            <PrizeList
                                emptyMessage={this.getMsg('epic.esport.prizes.empty')}
                                error={prizeState.hasError}
                                displayName={displayName}
                                errorMessage={this.getMsg('errors.esport.prizes.generic.error')}
                                identityTaxVerified={prizeState.identityTaxVerified}
                                loading={prizeState.isLoading}
                                messages={messages}
                                history={history}
                                getInterviewUrl={this.refreshInterviewUrl}
                                offlinePayment={prizeState.offlinePayment}
                                isValidationTaxProfile={taxProfileState.form && !!taxProfileState.form.recordIdentityKey}
                                prizes={sortedPrizes}
                            />
                        </Tabs.Tab>
                        {
                            isTaxProfileActive && <Tabs.Tab title={this.getMsg('epic.esport.prizes.tab.tax')}>
                                <TaxProfile firstCanRetakePrize={firstCanRetakePrize} jumpToTab={jumpToTab} />
                            </Tabs.Tab>
                        }
                    </Tabs>

                </div>
            </div>
        );
    }
}

